<template>
  <div class="mx">
    <div class="mx__intro">
      <div class="pf-section-title">{{ $t("match.title") }}</div>
      <p class="mx__sub">
        {{ $t("match.sub") }}
      </p>
    </div>

    <!-- O aviso vem antes das opções: a consequência precisa ser lida antes da
         escolha, não depois. Só aparece para quem já tem turma. -->
    <div v-if="linked" class="mx__warn">
      <v-icon size="20" color="#B5421E" class="mx__warn-icon"
        >mdi-alert-outline</v-icon
      >
      <div>
        <div class="mx__warn-title">{{ $t("match.warnTitle") }}</div>
        <i18n path="match.warnText" tag="p" class="mx__warn-text">
          <template #strong>
            <strong>{{ $t("match.warnStrong") }}</strong>
          </template>
        </i18n>
      </div>
    </div>

    <open-request-notice :request="openRequest" @canceled="loadMatch" />

    <!-- Card 86ak6k3u2: quem veio do formato solo nunca respondeu frequência
         nem modalidade — o cadastro dele pulou as duas perguntas. Elas definem
         a turma, então são pedidas aqui, antes de qualquer solicitação, em vez
         de a plataforma escolher por ele. -->
    <div v-if="needsStudyPreferences" class="mx__gate mx__gate--form">
      <v-icon size="30" color="#E8276F">mdi-clipboard-text-outline</v-icon>
      <div class="mx__gate-title">{{ $t("match.gateTitle") }}</div>
      <p class="mx__gate-text">
        {{ $t("match.gateText") }}
      </p>

      <div class="mx__gate-fields">
        <div class="mx__field">
          <div class="pf-field-label">{{ $t("match.frequency") }}</div>
          <v-select
            v-model="prefFrequency"
            :items="preferenceFrequencyOptions"
            item-text="text"
            item-value="value"
            :placeholder="$t('match.select')"
            color="#E8276F"
            item-color="#E8276F"
            outlined
            dense
            hide-details
          />
        </div>

        <div class="mx__field">
          <div class="pf-field-label">{{ $t("match.modality") }}</div>
          <v-select
            v-model="prefModality"
            :items="preferenceModalityOptions"
            item-text="text"
            item-value="value"
            :placeholder="$t('match.select')"
            color="#E8276F"
            item-color="#E8276F"
            outlined
            dense
            hide-details
          />
        </div>
      </div>

      <button
        type="button"
        class="mx__gate-btn"
        :disabled="!canSavePreferences || savingPreferences"
        @click="saveStudyPreferences"
      >
        <v-progress-circular
          v-if="savingPreferences"
          indeterminate
          size="18"
          width="2"
          color="#fff"
          class="mr-2"
        />
        {{ $t("match.saveContinue") }}
      </button>
    </div>

    <template v-else>
      <!-- Retorno de 18/10 (card 17trt6jdacg): com selects iguais aos da
           preferência, o filtro parecia a mesma informação repetida. Vira uma
           faixa de botões, com cara de filtro de busca. -->
      <div class="mx__filters">
        <div class="mx__filters-head">
          <i class="mdi mdi-filter-variant" aria-hidden="true"></i>
          {{ $t("match.filterTitle") }}
          <span class="mx__filters-hint">{{ $t("match.filterHint") }}</span>
        </div>

        <div class="mx__filter" role="group" :aria-label="$t('match.modality')">
          <span class="mx__filter-label">{{ $t("match.modality") }}</span>
          <button
            v-for="option in modalityPills"
            :key="`m-${option.value}`"
            type="button"
            class="mx__pill"
            :class="{ 'mx__pill--on': modality === option.value }"
            :aria-pressed="String(modality === option.value)"
            @click="modality = option.value"
          >
            {{ option.text }}
          </button>
        </div>

        <!-- Estudo independente não tem turma nem professor fixo, logo não tem
           frequência a escolher. -->
        <div
          v-if="!isIndependent"
          class="mx__filter"
          role="group"
          :aria-label="$t('match.frequency')"
        >
          <span class="mx__filter-label">{{ $t("match.frequency") }}</span>
          <button
            v-for="option in frequencyPills"
            :key="`f-${option.value}`"
            type="button"
            class="mx__pill"
            :class="{ 'mx__pill--on': frequency === option.value }"
            :aria-pressed="String(frequency === option.value)"
            @click="frequency = option.value"
          >
            {{ option.text }}
          </button>
        </div>
      </div>

      <!-- ---------- Estudo independente ---------- -->
      <div v-if="isIndependent" class="mx__solo">
        <div class="mx__solo-title">{{ $t("match.independent") }}</div>
        <p class="mx__solo-text">
          {{ $t("match.independentText") }}
        </p>
        <p v-if="linked" class="mx__solo-text mx__solo-text--warn">
          {{ $t("match.independentWarn") }}
        </p>

        <button
          type="button"
          class="mx__solo-btn"
          :disabled="switching"
          @click="confirmIndependent"
        >
          <v-progress-circular
            v-if="switching"
            indeterminate
            size="18"
            width="2"
            color="#fff"
            class="mr-2"
          />
          {{ $t("match.independentBtn") }}
        </button>
      </div>

      <!-- ---------- Professores e turmas ---------- -->
      <template v-else>
        <page-skeleton v-if="loading" variant="cards" :rows="3" class="mt-4" />

        <!-- Sem horário cadastrado o match não tem com o que cruzar: nenhuma
           opção apareceria, nunca, e sem dizer por quê. -->
        <div v-else-if="needsAvailability" class="mx__gate">
          <v-icon size="30" color="#E8276F">mdi-calendar-clock-outline</v-icon>
          <div class="mx__gate-title">
            {{ $t("match.noAvailabilityTitle") }}
          </div>
          <p class="mx__gate-text">
            {{ $t("match.noAvailabilityText") }}
          </p>
          <button type="button" class="mx__gate-btn" @click="goToAvailability">
            {{ $t("match.addTimes") }}
          </button>
        </div>

        <section v-if="teachers.length" class="mx__section">
          <div class="mx__section-title">
            {{ $t("match.teachers") }}
          </div>
          <div class="mx__grid">
            <MatchedPartnerCard
              v-for="(teacher, index) in teachers"
              :key="`t-${index}`"
              :item="teacher"
              :limit="limit"
              :has-pending-trial="hasPendingTrial"
              :trial-done="trialTeacherIds.includes(String(teacher.id))"
              :blocked="!!openRequest"
              @requestJoin="onJoinTeacher"
            />
          </div>
        </section>

        <section v-if="groups.length" class="mx__section">
          <div class="mx__section-title">{{ $t("match.groups") }}</div>
          <div class="mx__grid">
            <MatchedGroupCard
              v-for="(group, index) in groups"
              :key="`g-${index}`"
              :item="group"
              :student-format="prefModality"
              :student-frequency="prefFrequency"
              :blocked="!!openRequest"
              @requestJoin="onJoinGroup"
            />
          </div>
        </section>

        <div
          v-if="
            !loading && !needsAvailability && !teachers.length && !groups.length
          "
          class="mx__empty"
        >
          <v-icon size="30" color="#E8276F">mdi-account-search-outline</v-icon>
          <div class="mx__empty-title">{{ $t("match.emptyTitle") }}</div>
          <div class="mx__empty-text">
            {{ $t("match.emptyText") }}
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import customerApi from "@/shared/pitchCustomerApi";
import MatchedPartnerCard from "@/components/connect/partner/MatchedPartnerCard.vue";
import MatchedGroupCard from "@/components/connect/customer/MatchedGroupCard.vue";
import OpenRequestNotice from "@/components/connect/customer/OpenRequestNotice.vue";
import {
  GROUP_FORMAT_OPTIONS,
  GROUP_FREQUENCY_OPTIONS,
} from "@/shared/classPresentation";

// "Estudo independente" nao e um formato de turma: nao ha professor nem turma
// para buscar, entao ele nao vai como filtro para o match.
const INDEPENDENT = "independent";

export default {
  name: "MatchExplorer",

  components: { MatchedPartnerCard, MatchedGroupCard, OpenRequestNotice },

  props: {
    // O aluno esta vinculado a alguma turma hoje? Decide o aviso de desvinculo.
    linked: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      modality: null,
      frequency: null,
      teachers: [],
      groups: [],
      limit: 0,
      // Card 86akp2bkg: solicitação aguardando resposta, vinda do match.
      openRequest: null,
      // Card 86aje36nz: experimental em aberto e professores com quem o aluno
      // já fez experimental. Sem isto o botão ficava ativo nesta aba e só a
      // API recusava -- a página de turmas já desabilitava.
      hasPendingTrial: false,
      trialTeacherIds: [],
      loading: false,
      switching: false,
      needsAvailability: false,
      // Card 86ak6k3u2: o aluno vindo do formato solo precisa responder
      // frequência e modalidade antes de pedir turma ou professor.
      needsStudyPreferences: false,
      prefFrequency: null,
      prefModality: null,
      savingPreferences: false,
      // A ida automatica para a aba de horarios acontece uma vez por sessao da
      // tela. Depois disso o aluno esta ciente e pode ficar aqui se quiser.
      redirected: false,
      timer: null,
    };
  },

  computed: {
    modalityOptions() {
      return [
        ...GROUP_FORMAT_OPTIONS,
        { text: this.$t("match.independent"), value: INDEPENDENT },
      ];
    },
    frequencyOptions() {
      return GROUP_FREQUENCY_OPTIONS.filter((option) =>
        [1, 2].includes(option.value)
      );
    },
    // "Todas" é o filtro vazio (null), como o clearable dos selects antigos.
    modalityPills() {
      return [{ text: this.$t("match.all"), value: null }].concat(
        this.modalityOptions
      );
    },
    frequencyPills() {
      return [{ text: this.$t("match.all"), value: null }].concat(
        this.frequencyOptions
      );
    },
    // Estas são as opções do PERFIL, não do filtro: aqui não existe "Todas"
    // nem "Estudo independente" — é justamente o que o aluno está deixando
    // para trás. Os valores espelham os enums da API (PerWeek e Formats).
    preferenceFrequencyOptions() {
      return GROUP_FREQUENCY_OPTIONS.filter((option) =>
        [1, 2, 3, 4].includes(option.value)
      );
    },
    preferenceModalityOptions() {
      return GROUP_FORMAT_OPTIONS;
    },
    isIndependent() {
      return this.modality === INDEPENDENT;
    },
    canSavePreferences() {
      return !!this.prefFrequency && !!this.prefModality;
    },
  },

  watch: {
    modality() {
      // Independente nao tem frequencia; limpa para nao ficar um filtro
      // invisivel valendo quando o aluno voltar para uma modalidade de turma.
      if (this.isIndependent) {
        this.frequency = null;
        return;
      }
      this.scheduleLoad();
    },
    frequency() {
      this.scheduleLoad();
    },
  },

  created() {
    this.loadMatch();
  },

  beforeDestroy() {
    clearTimeout(this.timer);
  },

  methods: {
    scheduleLoad() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.loadMatch(), 300);
    },
    async loadMatch() {
      if (this.isIndependent) return;

      this.loading = true;

      try {
        const res = await customerApi.doAMatch({
          format: this.modality ?? undefined,
          frequency: this.frequency ?? undefined,
        });

        this.needsStudyPreferences = !!res.data.needsStudyPreferences;

        // Enquanto faltar a resposta do perfil, não há match para mostrar nem
        // aba de horários para cobrar: a pergunta de agora é outra.
        if (this.needsStudyPreferences) {
          this.teachers = [];
          this.groups = [];
          this.needsAvailability = false;
          return;
        }

        this.needsAvailability = !!res.data.needsAvailability;
        this.teachers = Array.isArray(res.data.teachers)
          ? res.data.teachers
          : [];
        this.groups = Array.isArray(res.data.groups) ? res.data.groups : [];
        this.limit = res.data.limit;
        this.openRequest = res.data.openRequest || null;
        this.hasPendingTrial = !!res.data.hasPendingTrial;
        this.trialTeacherIds = Array.isArray(res.data.trialTeacherIds)
          ? res.data.trialTeacherIds.map(String)
          : [];

        // Sem horarios o aluno nao tem o que fazer nesta aba: leva ele direto
        // para onde o bloqueio se resolve, uma vez so.
        if (this.needsAvailability && !this.redirected) {
          this.redirected = true;
          this.goToAvailability();
        }
      } catch (error) {
        this.teachers = [];
        this.groups = [];
        this.$root.$emit("display-alert", {
          text: this.$t("match.loadFailed"),
          type: 1,
        });
      } finally {
        this.loading = false;
      }
    },
    goToAvailability() {
      this.$emit("needs-availability");
    },
    // Card 86ak6k3u2: grava frequência e modalidade no perfil e refaz o match.
    // A API recusa o pedido de turma enquanto isso não estiver definido, então
    // este passo não é só de tela.
    async saveStudyPreferences() {
      if (!this.canSavePreferences || this.savingPreferences) return;

      this.savingPreferences = true;

      try {
        await customerApi.saveStudyPreferences({
          perWeek: this.prefFrequency,
          formatGoal: this.prefModality,
        });

        // O que o aluno acabou de responder vira o filtro inicial da busca —
        // é a turma que ele disse querer.
        this.modality = this.prefModality;
        this.frequency =
          this.prefFrequency === 1 || this.prefFrequency === 2
            ? this.prefFrequency
            : null;

        this.needsStudyPreferences = false;
        await this.loadMatch();
      } catch (error) {
        this.$root.$emit("display-alert", {
          text: error?.response?.data?.message || this.$t("match.saveFailed"),
          type: 1,
        });
      } finally {
        this.savingPreferences = false;
      }
    },
    // Chamado pelo pai quando o aluno volta para esta aba: os horarios podem ter
    // acabado de ser cadastrados, e a lista vazia de antes nao vale mais.
    reload() {
      this.loadMatch();
    },
    // O card já confirma o horário; aqui confirmamos a troca de turma, que é a
    // consequência que o aluno pode não ter em mente.
    confirmSwitch(what) {
      if (!this.linked) return true;
      return window.confirm(this.$t("match.switchConfirm", { what }));
    },
    async onJoinTeacher(item) {
      if (
        !this.confirmSwitch(
          item.experimental
            ? this.$t("match.trialRequest")
            : this.$t("match.linkRequest")
        )
      ) {
        return;
      }

      try {
        const res = await customerApi.requestJoin({
          partnerId: item.id,
          hours: item.hours,
          experimental: !!item.experimental,
        });
        this.$root.$emit("display-alert", {
          text: `${res.data.message} 😊`,
          type: 0,
        });
        this.$emit("requested");
        await this.loadMatch();
      } catch (err) {
        this.$root.$emit("display-alert", {
          text: err?.response?.data?.message || this.$t("match.requestFailed"),
          type: 1,
        });
      }
    },
    async onJoinGroup(item) {
      if (
        !this.confirmSwitch(this.$t("match.joinRequest", { code: item.cod }))
      ) {
        return;
      }

      try {
        const res = await customerApi.requestJoin({ classId: item.id });
        this.$root.$emit("display-alert", {
          text: `${res.data.message} 😊`,
          type: 0,
        });
        this.$emit("requested");
        await this.loadMatch();
      } catch (err) {
        this.$root.$emit("display-alert", {
          text: err?.response?.data?.message || this.$t("match.requestFailed"),
          type: 1,
        });
      }
    },
    async confirmIndependent() {
      const warning = this.linked ? this.$t("match.independentWarning") : "";

      if (!window.confirm(this.$t("match.independentConfirm", { warning }))) {
        return;
      }

      this.switching = true;

      try {
        const res = await customerApi.switchToIndependentStudy();
        this.$root.$emit("display-alert", {
          text: res.data.message,
          type: 0,
        });
        this.$emit("changed");
      } catch (err) {
        this.$root.$emit("display-alert", {
          text:
            err?.response?.data?.message || this.$t("match.independentFailed"),
          type: 1,
        });
      } finally {
        this.switching = false;
      }
    },
  },
};
</script>

<style scoped>
.mx {
  --accent: var(--dk-pink, #e8276f);
  --ink: var(--dk-ink, #231a2e);
  --muted: var(--dk-muted, #6b6472);
  --line: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09);
}
.mx__sub {
  margin: 6px 0 0;
  max-width: 62ch;
  font-size: 0.88rem;
  line-height: 1.5;
  color: var(--muted);
}

/* aviso de desvinculo */
.mx__warn {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 16px;
  padding: 14px 16px;
  border-radius: 14px;
  background: rgba(255, 93, 42, 0.08);
  border: 1px solid rgba(255, 93, 42, 0.25);
}
.mx__warn-icon {
  flex: 0 0 auto;
  margin-top: 1px;
}
.mx__warn-title {
  font-size: 0.9rem;
  font-weight: 800;
  color: var(--dk-ember-deep, #b5421e);
}
.mx__warn-text {
  margin: 4px 0 0;
  max-width: 62ch;
  font-size: 0.84rem;
  line-height: 1.5;
  color: var(--dk-ember-deep, #7a3517);
}

.mx__filters {
  display: grid;
  gap: 10px;
  margin-top: 18px;
  padding: 14px 16px;
  border-radius: 14px;
  background: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.03);
}
.mx__filters-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 8px;
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--ink);
}
.mx__filters-head .mdi {
  color: var(--accent);
}
.mx__filters-hint {
  font-size: 0.78rem;
  font-weight: 400;
  color: var(--muted);
}
.mx__filter {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}
.mx__filter-label {
  min-width: 92px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--muted);
}
.mx__pill {
  padding: 5px 12px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: var(--dk-surface, #fff);
  font: inherit;
  font-size: 0.8rem;
  color: var(--ink);
  cursor: pointer;
}
.mx__pill:hover {
  border-color: rgba(232, 39, 111, 0.4);
}
.mx__pill--on {
  border-color: var(--accent);
  background: var(--accent);
  color: #fff;
}
.mx__pill:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}
.mx__field {
  flex: 1 1 220px;
  min-width: 180px;
  max-width: 320px;
}

/* estudo independente */
.mx__solo {
  margin-top: 18px;
  padding: 20px;
  border-radius: 16px;
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line);
}
.mx__solo-title {
  font-size: 1rem;
  font-weight: 800;
  color: var(--ink);
}
.mx__solo-text {
  margin: 8px 0 0;
  max-width: 62ch;
  font-size: 0.88rem;
  line-height: 1.55;
  color: var(--muted);
}
.mx__solo-text--warn {
  color: var(--dk-ember-deep, #b5421e);
  font-weight: 600;
}
.mx__solo-btn {
  display: inline-flex;
  align-items: center;
  margin-top: 16px;
  padding: 11px 20px;
  border: none;
  border-radius: 12px;
  background: var(--accent);
  color: #fff;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  transition: filter 0.15s ease;
}
.mx__solo-btn:hover:not(:disabled) {
  filter: brightness(1.06);
}
.mx__solo-btn:disabled {
  opacity: 0.65;
  cursor: default;
}
.mx__solo-btn:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

/* sem horarios: bloqueio explicado, com a saida junto */
.mx__gate {
  margin-top: 24px;
  padding: 32px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: var(--dk-surface, #fff);
  border: 1px solid rgba(232, 39, 111, 0.25);
  border-radius: 16px;
}
.mx__gate-title {
  margin-top: 10px;
  font-size: 1rem;
  font-weight: 800;
  color: var(--ink);
}
.mx__gate-text {
  margin: 6px 0 0;
  max-width: 48ch;
  font-size: 0.86rem;
  line-height: 1.55;
  color: var(--muted);
}
.mx__gate-btn {
  margin-top: 16px;
  padding: 11px 20px;
  border: none;
  border-radius: 12px;
  background: var(--accent);
  color: #fff;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  transition: filter 0.15s ease;
}
.mx__gate-btn {
  display: inline-flex;
  align-items: center;
}
.mx__gate-btn:hover:not(:disabled) {
  filter: brightness(1.06);
}
.mx__gate-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.mx__gate-btn:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}
/* Os dois campos do card 86ak6k3u2 ficam lado a lado, como os filtros. */
.mx__gate-fields {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
  width: 100%;
  margin-top: 18px;
  text-align: left;
}

/* resultados */
.mx__section {
  margin-top: 24px;
}
.mx__section-title {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted);
  margin-bottom: 12px;
}
.mx__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}
.mx__empty {
  margin-top: 24px;
  padding: 32px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line);
  border-radius: 16px;
}
.mx__empty-title {
  margin-top: 10px;
  font-size: 1rem;
  font-weight: 700;
  color: var(--ink);
}
.mx__empty-text {
  margin-top: 6px;
  max-width: 44ch;
  font-size: 0.86rem;
  line-height: 1.5;
  color: var(--muted);
}

@media (prefers-reduced-motion: reduce) {
  .mx__solo-btn,
  .mx__gate-btn {
    transition: none;
  }
}
</style>
